import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { ShieldCheck, ShieldAlert, AlertTriangle, CheckCircle, Calendar, Clock } from 'lucide-react';

const money0 = (n) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0,
}).format(n || 0);
const money2 = (n) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD',
}).format(n || 0);

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch { return iso; }
};


/**
 * VestingStatusPanel — FERS vesting tracker per 5 U.S.C. § 8432(g).
 *
 * Displays:
 *   • Threshold basis (3 yr standard, 5 yr congressional/judicial, 2 yr BRS)
 *   • Years of service & progress bar
 *   • If vested: success state with vesting date
 *   • If NOT vested: days/years remaining, projected vesting date,
 *     dollar amount at risk (agency auto 1% + earnings)
 */
export const VestingStatusPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/participant/vesting-status');
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || 'Unable to load vesting status.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="panel p-5 animate-pulse" data-testid="vesting-loading">
        <div className="h-4 bg-gray-100 rounded w-40 mb-3" />
        <div className="h-24 bg-gray-50 rounded" />
      </div>
    );
  }
  if (err) {
    return (
      <div className="panel p-5 border-red-200" data-testid="vesting-error">
        <p className="text-sm text-red-600">{err}</p>
      </div>
    );
  }
  if (!data) return null;

  const isVested = data.isVested;
  const barColor = isVested ? '#3B8C6E' : '#B45309'; // emerald for vested, amber for in-progress
  const StatusIcon = isVested ? ShieldCheck : ShieldAlert;

  return (
    <div className="panel p-0 overflow-hidden" data-testid="vesting-status-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#E2E5E9] bg-[#FAFBFC]">
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ backgroundColor: isVested ? '#ECFDF5' : '#FFFBEB', border: `1px solid ${barColor}30` }}
          >
            <StatusIcon size={15} style={{ color: barColor }} />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-[#1B2A4A] uppercase tracking-wider">
              Vesting Status
            </h2>
            <p className="text-[11px] text-[#6B7280] mt-0.5" data-testid="vesting-threshold-basis">
              {data.thresholdBasis}
            </p>
          </div>
        </div>
        <span
          className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-wider px-2 py-1 rounded border"
          style={{
            backgroundColor: isVested ? '#ECFDF5' : '#FFFBEB',
            borderColor: isVested ? '#A7F3D0' : '#FDE68A',
            color: barColor,
          }}
          data-testid="vesting-badge"
        >
          {isVested ? 'FULLY VESTED' : 'IN PROGRESS'}
        </span>
      </div>

      {/* Body */}
      <div className="p-5 space-y-4">
        {/* Progress bar + metrics */}
        <div data-testid="vesting-progress-section">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-medium text-[#6B7280] uppercase tracking-wider">
              Progress Toward Vesting
            </span>
            <span className="font-mono text-xs font-semibold text-[#1B2A4A]" data-testid="vesting-progress-pct">
              {data.progressPct.toFixed(1)}%
            </span>
          </div>
          <div className="w-full h-2.5 bg-[#F0F1F3] rounded-sm overflow-hidden">
            <div
              className="h-full rounded-sm transition-all"
              style={{ width: `${data.progressPct}%`, backgroundColor: barColor }}
              data-testid="vesting-progress-bar"
            />
          </div>
          <div className="flex items-center justify-between mt-1 text-[10px] font-mono text-[#9CA3AF]">
            <span data-testid="vesting-years-served">
              {data.yearsOfService.toFixed(2)} yrs served
            </span>
            <span data-testid="vesting-threshold">
              Threshold: {data.threshold} yrs
            </span>
          </div>
        </div>

        {/* State-specific display */}
        {isVested ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded px-4 py-3" data-testid="vested-success-notice">
            <div className="flex items-start gap-2.5">
              <CheckCircle size={15} className="text-emerald-600 mt-0.5 shrink-0" />
              <div className="text-xs text-emerald-800 leading-relaxed">
                <p className="font-semibold text-sm">
                  Fully vested as of <span data-testid="vesting-vested-date">{fmtDate(data.vestingDate)}</span>.
                </p>
                <p className="mt-1">
                  All agency contributions belong to you regardless of when you separate from
                  federal service. (5 U.S.C. § 8432(g))
                </p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Remaining time + projected date */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded p-3" data-testid="vesting-remaining-card">
                <div className="flex items-center gap-1.5 mb-1">
                  <Clock size={10} className="text-[#9CA3AF]" />
                  <span className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Time Remaining</span>
                </div>
                <div className="font-mono text-lg font-bold text-[#1B2A4A]" data-testid="vesting-remaining-value">
                  {data.daysRemaining >= 365
                    ? `${data.yearsRemaining.toFixed(2)} yrs`
                    : `${data.daysRemaining} days`}
                </div>
              </div>
              <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded p-3" data-testid="vesting-date-card">
                <div className="flex items-center gap-1.5 mb-1">
                  <Calendar size={10} className="text-[#9CA3AF]" />
                  <span className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">Projected Vesting Date</span>
                </div>
                <div className="font-mono text-sm font-bold text-[#1B2A4A]" data-testid="vesting-projected-date">
                  {fmtDate(data.vestingDate)}
                </div>
              </div>
            </div>

            {/* At-risk amount */}
            <div className="bg-red-50 border border-red-200 rounded px-4 py-3" data-testid="vesting-at-risk-notice">
              <div className="flex items-start gap-2.5">
                <AlertTriangle size={15} className="text-red-600 mt-0.5 shrink-0" />
                <div className="text-xs text-red-800 leading-relaxed">
                  <p className="font-semibold text-sm text-red-900">
                    At Risk: <span data-testid="vesting-at-risk-amount">{money2(data.atRiskAmount)}</span>
                  </p>
                  <p className="mt-1">
                    If you separate today, agency automatic 1% contributions and their earnings
                    (<b data-testid="vesting-auto-balance">{money2(data.autoOneBalance)}</b>, of which{' '}
                    <b data-testid="vesting-auto-earnings">{money2(data.autoOneEarnings)}</b> is earnings)
                    would be forfeited. (5 U.S.C. § 8432(g); 5 C.F.R. § 1603.2)
                  </p>
                  <p className="mt-1 text-red-700">
                    Note: Your own employee contributions are <b>always vested</b> from day one —
                    forfeiture only applies to the agency automatic 1% contribution.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Always show auto-balance summary for transparency */}
        <div className="text-[10px] font-mono text-[#9CA3AF] flex items-center justify-between pt-2 border-t border-[#F0F1F3]">
          <span>Agency Auto 1% balance: {money0(data.autoOneBalance)}</span>
          <span data-testid="vesting-citation">{data.citation}</span>
        </div>
      </div>
    </div>
  );
};

export default VestingStatusPanel;
